import { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useLadder } from '../contexts/LadderContext';
import { useAuth } from '../contexts/AuthContext';
import { Swords, Plus, X, Trash2, Calendar, MapPin, MessageSquare, AlertTriangle, Zap, Trophy, Pencil } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper for label class based on language
const getLabelClass = (language) => language === 'el' 
  ? 'text-xs tracking-wider text-muted-foreground mb-3 block'
  : 'text-xs uppercase tracking-wider text-muted-foreground mb-3 block';

export default function AdminMatches() {
  const { t, language } = useLanguage();
  const { ladder, players, matches, addMatch, updateMatch, deleteMatch, getPlayerById, refreshLadder } = useLadder();
  const { token } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [editingMatch, setEditingMatch] = useState(null);
  const [form, setForm] = useState({
    team_a_player1_id: '',
    team_a_player2_id: '',
    team_b_player1_id: '',
    team_b_player2_id: '',
    sets: [
      { team_a: '', team_b: '' },
      { team_a: '', team_b: '' }
    ],
    date: '',
    court: '',
    comments: ''
  });

  const selectedPlayerIds = [
    form.team_a_player1_id,
    form.team_a_player2_id,
    form.team_b_player1_id,
    form.team_b_player2_id
  ].filter(Boolean);

  const getAvailablePlayers = (currentField) => {
    const currentValue = form[currentField];
    return players.filter(p => 
      p.id === currentValue || !selectedPlayerIds.includes(p.id)
    );
  };

  const handleSetChange = (index, field, value) => {
    const newSets = [...form.sets];
    newSets[index][field] = value;
    setForm({ ...form, sets: newSets });
  };

  // Calculate match status based on sets
  const getMatchStatus = () => {
    const validSets = form.sets.filter(s => s.team_a !== '' && s.team_b !== '');
    if (validSets.length === 0) return { valid: false, message: t('enterScores') };
    
    let teamASets = 0;
    let teamBSets = 0;
    
    for (const set of validSets) {
      const a = parseInt(set.team_a);
      const b = parseInt(set.team_b);
      if (isNaN(a) || isNaN(b)) continue;
      if (a === b) return { valid: false, message: t('setScoresTied') };
      if (a > b) teamASets++;
      else teamBSets++;
    }
    
    // Check for winner (need 2 sets)
    if (teamASets >= 2) {
      return { valid: true, winner: 'A', teamASets, teamBSets, needsThirdSet: false };
    }
    if (teamBSets >= 2) {
      return { valid: true, winner: 'B', teamASets, teamBSets, needsThirdSet: false };
    }
    
    // 1-1 tie, need super tiebreak
    if (teamASets === 1 && teamBSets === 1 && validSets.length === 2) {
      return { 
        valid: false, 
        message: t('tiedAddTiebreak'),
        needsThirdSet: true,
        teamASets,
        teamBSets
      };
    }
    
    // Still in progress
    if (validSets.length < 2) {
      return { 
        valid: false, 
        message: t('atLeastTwoSets'),
        teamASets,
        teamBSets
      };
    }
    
    return { valid: false, message: t('invalidMatch'), teamASets, teamBSets };
  };

  const matchStatus = getMatchStatus();

  const addThirdSet = () => {
    if (form.sets.length < 3) {
      setForm({ ...form, sets: [...form.sets, { team_a: '', team_b: '' }] });
    }
  };

  const removeSet = (index) => {
    if (form.sets.length > 2) {
      const newSets = form.sets.filter((_, i) => i !== index);
      setForm({ ...form, sets: newSets });
    }
  };

  const resetForm = () => {
    setEditingMatch(null);
    setForm({
      team_a_player1_id: '',
      team_a_player2_id: '',
      team_b_player1_id: '',
      team_b_player2_id: '',
      sets: [
        { team_a: '', team_b: '' },
        { team_a: '', team_b: '' }
      ],
      date: '',
      court: '',
      comments: ''
    });
  };

  const handleEditMatch = (match) => {
    setEditingMatch(match);
    setForm({
      team_a_player1_id: match.team_a_player1_id,
      team_a_player2_id: match.team_a_player2_id,
      team_b_player1_id: match.team_b_player1_id,
      team_b_player2_id: match.team_b_player2_id,
      sets: match.sets.map(s => ({ team_a: String(s.team_a), team_b: String(s.team_b) })),
      date: match.date || '',
      court: match.court || '',
      comments: match.comments || ''
    });
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.team_a_player1_id || !form.team_a_player2_id || 
        !form.team_b_player1_id || !form.team_b_player2_id) {
      toast.error(t('selectAllPlayers'));
      return;
    }

    if (!matchStatus.valid) {
      toast.error(matchStatus.message);
      return;
    }

    const validSets = form.sets.filter(s => s.team_a !== '' && s.team_b !== '');

    setLoading(true);
    try {
      const matchData = {
        team_a_player1_id: form.team_a_player1_id,
        team_a_player2_id: form.team_a_player2_id,
        team_b_player1_id: form.team_b_player1_id,
        team_b_player2_id: form.team_b_player2_id,
        sets: validSets.map(s => ({
          team_a: parseInt(s.team_a),
          team_b: parseInt(s.team_b)
        })),
        date: form.date || null,
        court: form.court || null,
        comments: form.comments || null
      };
      
      if (editingMatch) {
        // Update existing match
        await updateMatch(editingMatch.id, {
          sets: matchData.sets,
          date: matchData.date,
          court: matchData.court,
          comments: matchData.comments
        });
        toast.success(t('matchUpdated'));
      } else {
        // Create new match
        await addMatch(ladder.id, matchData);
        toast.success(t('matchAdded'));
      }
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMatch = async (matchId) => {
    if (!window.confirm(t('deleteMatchConfirm'))) return;
    
    try {
      await deleteMatch(matchId);
      toast.success(t('matchDeleted'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="admin-matches-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-2xl lg:text-3xl font-bold text-foreground mb-1">
          {t('matchEntry')}
        </h1>
        <p className="text-muted-foreground">{matches.length} {t('totalMatches').toLowerCase()}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Match Entry Form */}
        <div className="bg-card rounded-xl border border-border p-6" data-testid="match-form">
          <h2 className="font-heading text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
            <Swords className="h-5 w-5" />
            {editingMatch 
              ? t('editMatch')
              : t('submitMatch')
            }
            {editingMatch && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={resetForm}
                className="ml-auto text-muted-foreground"
              >
                <X className="h-4 w-4 mr-1" />
                {t('cancel')}
              </Button>
            )}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Team A */}
            <div className="p-4 bg-muted/50 rounded-lg border border-border">
              <Label className={getLabelClass(language)}>
                {t('teamA')}
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <Select
                  value={form.team_a_player1_id}
                  onValueChange={(v) => setForm({ ...form, team_a_player1_id: v })}
                >
                  <SelectTrigger data-testid="team-a-player1-select">
                    <SelectValue placeholder={t('selectPlayer')} />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailablePlayers('team_a_player1_id').map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={form.team_a_player2_id}
                  onValueChange={(v) => setForm({ ...form, team_a_player2_id: v })}
                >
                  <SelectTrigger data-testid="team-a-player2-select">
                    <SelectValue placeholder={t('selectPlayer')} />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailablePlayers('team_a_player2_id').map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Team B */}
            <div className="p-4 bg-muted/50 rounded-lg border border-border">
              <Label className={getLabelClass(language)}>
                {t('teamB')}
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <Select
                  value={form.team_b_player1_id}
                  onValueChange={(v) => setForm({ ...form, team_b_player1_id: v })}
                >
                  <SelectTrigger data-testid="team-b-player1-select">
                    <SelectValue placeholder={t('selectPlayer')} />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailablePlayers('team_b_player1_id').map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={form.team_b_player2_id}
                  onValueChange={(v) => setForm({ ...form, team_b_player2_id: v })}
                >
                  <SelectTrigger data-testid="team-b-player2-select">
                    <SelectValue placeholder={t('selectPlayer')} />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailablePlayers('team_b_player2_id').map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Set Scores - Best of 3 */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className={getLabelClass(language).replace('mb-3 block', '')}>
                  {t('setScores')} (Best of 3)
                </Label>
              </div>
              <div className="space-y-3">
                {form.sets.map((set, index) => (
                  <div key={`set-${index}`} className="flex items-center gap-2">
                    <span className={`text-sm font-medium w-20 ${
                      index === 2 ? 'text-amber-600 dark:text-amber-400 flex items-center gap-1' : 'text-muted-foreground'
                    }`}>
                      {index === 2 ? (
                        <>
                          <Zap className="h-3 w-3" />
                          STB
                        </>
                      ) : (
                        `Set ${index + 1}`
                      )}
                    </span>
                    <Input
                      type="number"
                      min="0"
                      max="15"
                      placeholder="A"
                      value={set.team_a}
                      onChange={(e) => handleSetChange(index, 'team_a', e.target.value)}
                      className="w-20 text-center"
                      data-testid={`set-${index}-team-a`}
                    />
                    <span className="text-muted-foreground">-</span>
                    <Input
                      type="number"
                      min="0"
                      max="15"
                      placeholder="B"
                      value={set.team_b}
                      onChange={(e) => handleSetChange(index, 'team_b', e.target.value)}
                      className="w-20 text-center"
                      data-testid={`set-${index}-team-b`}
                    />
                    {index === 2 && (
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeSet(index)}
                        className="text-red-500 hover:text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Match Status Indicator */}
              <div className={`mt-4 p-3 rounded-lg flex items-center gap-2 ${
                matchStatus.valid 
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400' 
                  : matchStatus.needsThirdSet
                    ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400'
                    : 'bg-muted text-muted-foreground'
              }`}>
                {matchStatus.valid ? (
                  <>
                    <Trophy className="h-4 w-4" />
                    <span className="text-sm font-medium">
                      {t('winnerLabel')} {t('team' + matchStatus.winner)} ({matchStatus.teamASets}-{matchStatus.teamBSets})
                    </span>
                  </>
                ) : matchStatus.needsThirdSet ? (
                  <>
                    <AlertTriangle className="h-4 w-4" />
                    <span className="text-sm font-medium flex-1">{matchStatus.message}</span>
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="outline"
                      onClick={addThirdSet}
                      className="gap-1"
                      data-testid="add-super-tiebreak"
                    >
                      <Plus className="h-3 w-3" />
                      <Zap className="h-3 w-3" />
                    </Button>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-4 w-4" />
                    <span className="text-sm">{matchStatus.message}</span>
                  </>
                )}
              </div>
            </div>

            {/* Optional Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1 text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  {t('date')}
                </Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="match-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1 text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  {t('court')}
                </Label>
                <Input
                  value={form.court}
                  onChange={(e) => setForm({ ...form, court: e.target.value })}
                  placeholder="Court 1"
                  data-testid="match-court-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-1 text-muted-foreground">
                <MessageSquare className="h-4 w-4" />
                {t('comments')}
              </Label>
              <Textarea
                value={form.comments}
                onChange={(e) => setForm({ ...form, comments: e.target.value })}
                placeholder={t('optionalNotes')}
                rows={2}
                data-testid="match-comments-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full gap-2 bg-green-600 hover:bg-green-700"
              disabled={loading || !matchStatus.valid}
              data-testid="submit-match-button"
            >
              {editingMatch ? <Pencil className="h-4 w-4" /> : <Swords className="h-4 w-4" />}
              {loading ? t('loading') : (editingMatch ? t('save') : t('submitMatch'))}
            </Button>
          </form>
        </div>

        {/* Recent Matches */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-foreground">
              {t('matchHistory')}
            </h2>
            <span className="text-xs text-muted-foreground">
              {t('sortedByDate')}
            </span>
          </div>
          
          {matches.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-xl border border-border">
              <Swords className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">{t('noMatches')}</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2" data-testid="matches-list">
              {matches.map((match, index) => {
                const teamAP1 = getPlayerById(match.team_a_player1_id);
                const teamAP2 = getPlayerById(match.team_a_player2_id);
                const teamBP1 = getPlayerById(match.team_b_player1_id);
                const teamBP2 = getPlayerById(match.team_b_player2_id);
                const sets = match.sets || [];
                const isSuperTiebreak = sets.length === 3 && 
                  sets.slice(0, 2).filter(s => s.team_a > s.team_b).length === 1;
                
                return (
                  <div 
                    key={match.id} 
                    className="bg-card rounded-lg border border-border p-4"
                    data-testid={`match-item-${index}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                              match.winner_team === 'A' 
                                ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' 
                                : 'bg-muted text-muted-foreground'
                            }`}>
                              {t('teamA')}
                            </span>
                            <span className="text-sm text-foreground truncate">
                              {teamAP1?.name?.split(' ')[0]} & {teamAP2?.name?.split(' ')[0]}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                              match.winner_team === 'B' 
                                ? 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400' 
                                : 'bg-muted text-muted-foreground'
                            }`}>
                              {t('teamB')}
                            </span>
                            <span className="text-sm text-foreground truncate">
                              {teamBP1?.name?.split(' ')[0]} & {teamBP2?.name?.split(' ')[0]}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            {sets.map((set, si) => (
                              <span key={`match-set-${si}`} className={`text-xs px-2 py-1 rounded ${
                                si === 2 && isSuperTiebreak 
                                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                                  : 'bg-muted text-foreground'
                              }`}>
                                {set.team_a}-{set.team_b}
                              </span>
                            ))}
                            {isSuperTiebreak && (
                              <Zap className="h-3 w-3 text-amber-600 dark:text-amber-400" />
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleEditMatch(match)}
                          className="text-muted-foreground hover:text-foreground flex-shrink-0"
                          data-testid={`edit-match-${index}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleDeleteMatch(match.id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex-shrink-0"
                          data-testid={`delete-match-${index}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
